import { ChangeDetectionStrategy, Component, computed, input, output, ViewEncapsulation } from "@angular/core";
import { ReportBuilderCell } from "./s25.document.report.builder.table.component";
import { Doc } from "./s25.document.const";
import { S25Util } from "../../../util/s25-util";

@Component({
    standalone: false,
    selector: "s25-ng-document-report-builder-cell",
    template: `
        @if (editable()) {
            <s25-popover
                [popoverTemplate]="editCellTemplate"
                [openTrigger]="'click'"
                [closeTrigger]="'click'"
                [inline]="false"
            >
                <ng-container [ngTemplateOutlet]="cellTemplate" />
            </s25-popover>
        } @else {
            <ng-container [ngTemplateOutlet]="cellTemplate" />
        }

        <ng-template #cellTemplate>
            <div class="style-container" [class.invalid]="!cell().valid()" [style]="cell().css()">
                @for (item of cell().display(); track $index) {
                    @if (item.type === "text") {
                        <span class="text">{{ item.value }}</span>
                    } @else if (item.type === "functionText") {
                        <span class="functionText">{{ item.value }}</span>
                    } @else if (item.type === "variable") {
                        <span class="variable">{{ item.value }}</span>
                    } @else if (item.type === "subreport" && !!cell().variable().subreportId) {
                        <s25-ng-document-report-builder
                            [documentId]="cell().variable().subreportId"
                            [editable]="false"
                            (reportBuilderChange)="cell().variable().subreport = $event; changed.emit()"
                        />
                    }
                }
            </div>
        </ng-template>

        <ng-template #editCellTemplate>
            <s25-ng-document-report-builder-cell-editor
                [cell]="cell()"
                [documentType]="documentType()"
                (changed)="changed.emit()"
                (widthChange)="widthChange.emit($event)"
            />
        </ng-template>
    `,
    styles: `
        :host {
            display: block;
            height: 100%;
            overflow: hidden;
        }

        .style-container {
            overflow: hidden;
            height: 100%;
            align-content: end;
            overflow-wrap: anywhere;
            padding: 0.25rem 0.5rem;
        }

        .style-container:has(s25-ng-document-report-builder) {
            padding: 0;

            &::after {
                --stripe-width: 1.5em;
                --stripe-color: #0001;
                content: "";
                background: repeating-linear-gradient(
                    -45deg,
                    var(--stripe-color),
                    var(--stripe-color) var(--stripe-width),
                    transparent var(--stripe-width),
                    transparent calc(var(--stripe-width) * 2)
                );
                position: absolute;
                height: 100%;
                width: 100%;
                top: 0;
                left: 0;
            }
        }

        .invalid {
            background-color: color-mix(in hsl, var(--red), transparent 85%);
        }

        .functionText {
            font-weight: bold;
            font-size: 0.9em;
            font-family: monospace;
        }

        .variable {
            font-size: 0.9em;
            font-family: monospace;
        }
    `,
    host: {
        "[attr.title]": "typeName()",
    },
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentReportBuilderCellComponent {
    documentType = input.required<Doc.SubreportTypeId>();
    cell = input.required<ReportBuilderCell>();
    editable = input.required<boolean>();

    typeName = computed(() => S25Util.firstCharToUpper(this.cell().variable().type()));

    changed = output<void>();
    widthChange = output<number>();
}
